import { useMutation, useQuery } from "@apollo/client";
import { Alert } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { ResolutionStatus } from "generated-graphql/graphql";
import { useDebounce } from "hooks/useDebounce";
import { useState } from "react";

import { EXCEEDANCE_REFETCH, ExceedanceKey } from "./exceedanceKeys";
import { ManifestCorrectionRow } from "./manifestCorrectionDisplay";
import { ManifestCorrectionPicker } from "./ManifestCorrectionPicker";
import { ManifestCorrectionsTable } from "./ManifestCorrectionsTable";
import { LINKABLE_MANIFEST_CORRECTIONS_QUERY } from "./MarkResolvedDialog";

const CHANGE_LINKED_CORRECTIONS_MUTATION = gql(`
  mutation ChangeGeneratorStatusExceedanceLinkedCorrections(
    $exceedance: GeneratorStatusExceedanceKeyInput!
    $addManifestDiscrepancyIds: [ID!]
    $removeManifestDiscrepancyIds: [ID!]
  ) {
    changeGeneratorStatusExceedanceLinkedCorrections(
      exceedance: $exceedance
      addManifestDiscrepancyIds: $addManifestDiscrepancyIds
      removeManifestDiscrepancyIds: $removeManifestDiscrepancyIds
    ) {
      id
      status
    }
  }
`);

function linkedCorrectionsOutcome(status: ResolutionStatus): string {
  if (status === ResolutionStatus.Resolved) {
    return "Every linked correction is complete, so the review is resolved.";
  }
  if (status === ResolutionStatus.Pending)
    return "The review is waiting on the linked corrections.";
  return "No corrections are linked, so the review is open again.";
}

type Props = {
  exceedanceKey: ExceedanceKey;
  tenantId: string;
  corrections: ManifestCorrectionRow[];
  /** Linking corrections replaces this submission as the review's follow-up. */
  linkedSubmissionTitle?: string | null;
};

/** The drawer's linked corrections, with Add and Remove. */
export function LinkedCorrections({
  exceedanceKey,
  tenantId,
  corrections,
  linkedSubmissionTitle,
}: Props) {
  const alerts = useAlerts();
  const [addOpen, setAddOpen] = useState(false);
  const [change, { loading }] = useMutation(CHANGE_LINKED_CORRECTIONS_MUTATION, {
    refetchQueries: EXCEEDANCE_REFETCH,
    awaitRefetchQueries: true,
  });

  const run = async (
    ids: { add?: string[]; remove?: string[] },
    done: string
  ): Promise<boolean> => {
    try {
      const { data } = await change({
        variables: {
          exceedance: exceedanceKey,
          addManifestDiscrepancyIds: ids.add,
          removeManifestDiscrepancyIds: ids.remove,
        },
      });
      const status = data?.changeGeneratorStatusExceedanceLinkedCorrections.status;
      alerts.success(status ? `${done} ${linkedCorrectionsOutcome(status)}` : done);
      return true;
    } catch (e) {
      alerts.error("Failed to update the linked corrections.", e);
      return false;
    }
  };

  return (
    <>
      <ManifestCorrectionsTable
        corrections={corrections}
        tenantId={tenantId}
        onAdd={() => setAddOpen(true)}
        onRemove={(c) => run({ remove: [c.id] }, "Correction removed.")}
        disabled={loading}
      />
      {/* Mounted only while open, so each open starts with no picks or search. */}
      {addOpen && (
        <AddLinkedCorrectionsDialog
          onClose={() => setAddOpen(false)}
          exceedanceKey={exceedanceKey}
          tenantId={tenantId}
          linkedIds={corrections.map((c) => c.id)}
          linkedSubmissionTitle={linkedSubmissionTitle}
          submitting={loading}
          onSubmit={async (ids) => {
            const ok = await run(
              { add: ids },
              ids.length === 1 ? "Correction linked." : "Corrections linked."
            );
            if (ok) setAddOpen(false);
          }}
        />
      )}
    </>
  );
}

function AddLinkedCorrectionsDialog({
  onClose,
  exceedanceKey,
  tenantId,
  linkedIds,
  linkedSubmissionTitle,
  submitting,
  onSubmit,
}: {
  onClose: () => void;
  exceedanceKey: ExceedanceKey;
  tenantId: string;
  linkedIds: string[];
  linkedSubmissionTitle?: string | null;
  submitting: boolean;
  onSubmit: (ids: string[]) => Promise<void>;
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  const trimmedSearch = debouncedSearch.trim();

  const { data, loading } = useQuery(LINKABLE_MANIFEST_CORRECTIONS_QUERY, {
    variables: {
      facilityId: exceedanceKey.facilityId,
      source: exceedanceKey.source,
      periodStart: exceedanceKey.periodStart,
      search: trimmedSearch || undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  const rows = (data?.generatorStatusExceedanceLinkableManifestCorrections ?? []).filter(
    (c) => !linkedIds.includes(c.id)
  );
  // Only visible picks are submitted, matching Mark resolved.
  const visibleIds = selectedIds.filter((id) => rows.some((r) => r.id === id));
  const hiddenSelectedCount = loading ? 0 : selectedIds.length - visibleIds.length;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 820, maxWidth: "90vw" } } }}
      title="Add linked corrections"
      description="The review status follows the linked corrections: resolved when every one is complete, pending while any is open."
      primaryActionButtonProps={{
        onClick: () => onSubmit(visibleIds),
        loading: submitting,
        loadingText: "Saving...",
        hideStartIcon: true,
        saveText: "Link corrections",
        disabled: submitting || visibleIds.length === 0,
      }}
      cancelButtonProps={{ disabled: submitting }}
    >
      {linkedSubmissionTitle && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Linking corrections unlinks the submission &ldquo;{linkedSubmissionTitle}&rdquo;.
        </Alert>
      )}
      <ManifestCorrectionPicker
        rows={rows}
        loading={loading}
        search={search}
        onSearchChange={setSearch}
        searching={trimmedSearch.length > 0}
        selectedIds={visibleIds}
        hiddenSelectedCount={hiddenSelectedCount}
        onSelectionChange={setSelectedIds}
        tenantId={tenantId}
      />
    </Dialog>
  );
}
